import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { initiativeApi } from '../services/softwareFactoryApi';
import type { InitiativeListParams } from '../services/softwareFactoryApi';
import type { RoadmapHorizon } from '../constants';
import type { ListResponse, SfInitiative, SfInitiativeCreate } from '../types';

export const initiativeKeys = {
  all: ['sdevInitiatives'] as const,
  list: (params?: Record<string, unknown>) => [...initiativeKeys.all, 'list', params] as const,
  detail: (id: string) => [...initiativeKeys.all, 'detail', id] as const,
  customers: (id: string) => [...initiativeKeys.all, 'customers', id] as const,
  okrObjectives: () => [...initiativeKeys.all, 'okrObjectives'] as const,
};

export function useInitiatives(
  params?: InitiativeListParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: initiativeKeys.list(params as Record<string, unknown>),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => initiativeApi.list(params),
    enabled: options?.enabled ?? true,
  });
}

export function useInitiative(id: string) {
  return useQuery({
    queryKey: initiativeKeys.detail(id),
    queryFn: () => initiativeApi.get(id),
    enabled: !!id,
  });
}

export function useCreateInitiative() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SfInitiativeCreate) => initiativeApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: initiativeKeys.all });
    },
  });
}

export function useUpdateInitiative() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SfInitiativeCreate> }) => initiativeApi.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: initiativeKeys.all });
      queryClient.invalidateQueries({ queryKey: initiativeKeys.detail(variables.id) });
    },
  });
}

export function useRebaselineInitiative() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => initiativeApi.rebaseline(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: initiativeKeys.all });
      queryClient.invalidateQueries({ queryKey: initiativeKeys.detail(id) });
    },
  });
}

/**
 * Move an initiative to a roadmap horizon (Now / Next / Later / none) with an
 * optimistic update across every cached initiative list, rolling back on error.
 * Used by the roadmap board's drag-to-rebucket interaction.
 */
export function useMoveInitiativeHorizon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roadmap_horizon }: { id: string; roadmap_horizon: RoadmapHorizon }) =>
      initiativeApi.update(id, { roadmap_horizon }),
    onMutate: async ({ id, roadmap_horizon }) => {
      await queryClient.cancelQueries({ queryKey: initiativeKeys.all });
      const snapshots = queryClient.getQueriesData<ListResponse<SfInitiative>>({ queryKey: initiativeKeys.all });
      for (const [key, data] of snapshots) {
        if (!data?.items) continue;
        queryClient.setQueryData<ListResponse<SfInitiative>>(key, {
          ...data,
          items: data.items.map((i) => (i.id === id ? { ...i, roadmap_horizon } : i)),
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      for (const [key, data] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, data);
      }
      // The global mutation-error handler shows the specific translated toast.
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: initiativeKeys.all });
    },
  });
}

/**
 * Reschedule an initiative's planned span (start_date / target_date) with an
 * optimistic patch across every cached initiative list, rolling back on error.
 * Used by the Roadmap Gantt's drag-to-reschedule interaction. Dates are ISO
 * `yyyy-MM-dd` strings (the API's planning-date format).
 */
export function useRescheduleInitiative() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, start_date, target_date }: { id: string; start_date: string; target_date: string }) =>
      initiativeApi.update(id, { start_date, target_date }),
    onMutate: async ({ id, start_date, target_date }) => {
      await queryClient.cancelQueries({ queryKey: initiativeKeys.all });
      const snapshots = queryClient.getQueriesData<ListResponse<SfInitiative>>({ queryKey: initiativeKeys.all });
      for (const [key, data] of snapshots) {
        if (!data?.items) continue;
        queryClient.setQueryData<ListResponse<SfInitiative>>(key, {
          ...data,
          items: data.items.map((i) => (i.id === id ? { ...i, start_date, target_date } : i)),
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      for (const [key, data] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, data);
      }
      // The global mutation-error handler shows the specific translated toast.
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: initiativeKeys.all });
      queryClient.invalidateQueries({ queryKey: initiativeKeys.detail(variables.id) });
    },
  });
}

export function useDeleteInitiative() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => initiativeApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: initiativeKeys.all });
    },
  });
}

export function useInitiativeCustomers(id: string) {
  return useQuery({
    queryKey: initiativeKeys.customers(id),
    queryFn: () => initiativeApi.listCustomers(id),
    enabled: !!id,
  });
}

/** Tenant OKR objectives (depth-0 tasks on OKR boards) for the initiative goal picker. */
export function useOkrObjectives(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: initiativeKeys.okrObjectives(),
    queryFn: () => initiativeApi.listOkrObjectives(),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useAddInitiativeCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      organization_id,
      contact_id,
      deal_id,
    }: {
      id: string;
      organization_id?: string | null;
      contact_id?: string | null;
      deal_id?: string | null;
    }) => initiativeApi.addCustomer(id, { organization_id, contact_id, deal_id }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: initiativeKeys.customers(variables.id) });
    },
  });
}

export function useRemoveInitiativeCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, customerId }: { id: string; customerId: string }) =>
      initiativeApi.removeCustomer(id, customerId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: initiativeKeys.customers(variables.id) });
    },
  });
}
